import { BROWSER_SESSION_BOUNDARY, getBrowserSession } from "src/lib/cw-browser-session";
import { UserProfile, ApiUserProfile } from "./types";
import { mapApiUserProfile } from "./mappers";

const PROFILE_DEDUPE_WINDOW_MS = 2000;
export const E2E_FEATURE_FLAGS_COOKIE = "caltana_e2e_feature_flags";

let browserProfileRequest: Promise<UserProfile> | null = null;
let browserProfileSnapshot: {
  expiresAt: number;
  profile: UserProfile;
  revision: number;
} | null = null;

export function resetBrowserProfileCache(): void {
  browserProfileRequest = null;
  browserProfileSnapshot = null;
}
if (typeof window !== "undefined") {
  window.addEventListener(BROWSER_SESSION_BOUNDARY, resetBrowserProfileCache);
}

function isE2eFeatureFlagOverrideAllowed(): boolean {
  return process.env.NODE_ENV === "test" || process.env.NEXT_PUBLIC_VERCEL_ENV === "local";
}

export function parseE2eFeatureFlagOverrides(
  rawCookieValue: string | null | undefined,
): Record<string, boolean> {
  if (!rawCookieValue) {
    return {};
  }

  try {
    const decodedValue = decodeURIComponent(rawCookieValue);
    const parsedValue = JSON.parse(decodedValue);

    if (!parsedValue || typeof parsedValue !== "object" || Array.isArray(parsedValue)) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(parsedValue).filter(
        (entry): entry is [string, boolean] => typeof entry[1] === "boolean",
      ),
    );
  } catch {
    return {};
  }
}

function getBrowserCookieValue(cookieName: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const prefix = `${cookieName}=`;
  const cookie = document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix));

  return cookie ? cookie.slice(prefix.length) : null;
}

async function getE2eFeatureFlagOverrides(): Promise<Record<string, boolean>> {
  if (!isE2eFeatureFlagOverrideAllowed()) {
    return {};
  }

  const rawCookieValue = getBrowserCookieValue(E2E_FEATURE_FLAGS_COOKIE);

  return parseE2eFeatureFlagOverrides(rawCookieValue);
}

async function applyE2eFeatureFlagOverrides(profile: UserProfile): Promise<UserProfile> {
  const featureFlagOverrides = await getE2eFeatureFlagOverrides();

  if (Object.keys(featureFlagOverrides).length === 0) {
    return profile;
  }

  return {
    ...profile,
    featureFlags: {
      ...profile.featureFlags,
      ...featureFlagOverrides,
    },
  };
}

/**
 * Fetches the current user's profile
 * @returns Promise with the user profile
 */
export async function getProfile(): Promise<UserProfile> {
  if (typeof window === "undefined") throw new Error("Use the server profile entry point");
  const session = getBrowserSession();
  const revision = session?.currentRevision ?? 0;
  const assertCurrent = () => {
    if (session && (session.currentRevision !== revision || session.transitionPending))
      throw new Error("Session changed");
  };
  assertCurrent();
  if (
    browserProfileSnapshot &&
    browserProfileSnapshot.revision === revision &&
    browserProfileSnapshot.expiresAt > Date.now()
  )
    return browserProfileSnapshot.profile;
  if (browserProfileRequest) return browserProfileRequest;
  const raw: Promise<ApiUserProfile> = fetch("/api/cw-consumer/profile", {
    credentials: "include",
    cache: "no-store",
    redirect: "error",
  }).then(async (response) => {
    if (!response.ok)
      throw new Error(response.status === 401 ? "Unauthorized" : "Profile unavailable");
    const value: unknown = await response.json();
    if (
      typeof value !== "object" ||
      value === null ||
      !("id" in value) ||
      !Number.isSafeInteger(value.id) ||
      Number(value.id) <= 0
    )
      throw new Error("Profile unavailable");
    return value as ApiUserProfile;
  });
  const request = raw
    .then((value) => {
      assertCurrent();
      return mapApiUserProfile(value);
    })
    .then(applyE2eFeatureFlagOverrides)
    .then((profile) => {
      assertCurrent();
      return profile;
    });
  const owned = request
    .then((profile) => {
      assertCurrent();
      browserProfileSnapshot = {
        expiresAt: Date.now() + PROFILE_DEDUPE_WINDOW_MS,
        profile,
        revision,
      };
      return profile;
    })
    .finally(() => {
      if (browserProfileRequest === owned) browserProfileRequest = null;
    });
  browserProfileRequest = owned;
  return owned;
}
